import {
  formatIsoDate,
  formatPrice,
  formatSigned,
  STOP_LOSS_EVENT_LABELS,
  type PositionView,
  type StopLossEvent,
} from '@stockstop/shared';
import { Modal } from '../../components/Modal.js';
import { useStopLossHistory } from '../../api/queries.js';

/**
 * Verlauf der Stop-Loss-Entscheidungen einer Position.
 *
 * Zeigt, **wie** nachjustiert wurde, nicht nur wo der Stop gerade steht. Jede Zeile
 * ist eine Entscheidung: der neue Kurs, woher er kam, und wie der Markt an diesem
 * Tag stand. Letzteres macht die Entscheidung im Rückblick lesbar — ein Stop bei
 * 252 sagt wenig, ein Stop bei 252 während der Kurs bei 288 stand, sagt etwas.
 */

interface HistoryDialogProps {
  position: PositionView | null;
  onClose: () => void;
}

const formatDateTime = (value: Date | string): string =>
  new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const EventRow = ({
  event,
  currency,
}: {
  event: StopLossEvent;
  currency: string | null;
}): React.JSX.Element => {
  /* Die Bewegung gegenüber dem vorherigen Stop, sofern es einen gab. */
  const delta =
    event.stopPrice !== null && event.previousStopPrice !== null
      ? event.stopPrice - event.previousStopPrice
      : null;

  return (
    <li className="history__row">
      <span className="history__time muted small">{formatDateTime(event.createdAt)}</span>

      <span className="history__what">
        <span className={`history__badge history__badge--${event.type}`}>
          {STOP_LOSS_EVENT_LABELS[event.type]}
        </span>
        {event.validUntil ? (
          <span className="muted small"> bis {formatIsoDate(event.validUntil)}</span>
        ) : null}
      </span>

      <span className="history__price">
        {event.stopPrice === null ? '—' : formatPrice(event.stopPrice, { currency })}
        {delta !== null && Math.abs(delta) > 0.001 ? (
          <span className={`small ${delta > 0 ? 'tone-ok' : 'tone-danger'}`}>
            {' '}
            {formatSigned(delta)}
          </span>
        ) : null}
      </span>

      <span className="history__context muted small">
        {event.referenceClose === null
          ? ''
          : `Kurs ${formatPrice(event.referenceClose, { currency, digits: 0 })}`}
      </span>
    </li>
  );
};

export const HistoryDialog = ({ position, onClose }: HistoryDialogProps): React.JSX.Element => {
  const history = useStopLossHistory(position?.id ?? null);
  const currency = position?.instrument.currency ?? null;

  return (
    <Modal
      isOpen={position !== null}
      title={`Verlauf${position ? ` · ${position.instrument.symbol}` : ''}`}
      onClose={onClose}
      actions={
        <button type="button" onClick={onClose}>
          Schließen
        </button>
      }
    >
      {history.isPending ? <p className="muted">Lädt …</p> : null}

      {history.isSuccess && history.data.length === 0 ? (
        <p className="muted small">
          Noch keine Entscheidungen festgehalten. Der Verlauf füllt sich, sobald du die
          Stop-Loss-Linie ziehst oder eine Order einbuchst.
        </p>
      ) : null}

      {history.isSuccess && history.data.length > 0 ? (
        <ul className="history">
          {history.data.map((event) => (
            <EventRow key={event.id} event={event} currency={currency} />
          ))}
        </ul>
      ) : null}
    </Modal>
  );
};
